import { useSuspenseQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useNavigate } from "react-router";
import { FinancingStagePath, financingStageQueryOptions, useSpaNavigate } from "shared-ui";
import { useCustomerAppConfig } from "undercarriage";
import { addError } from "@wayflyer/blackbox-fe";

import { useCreateFundingApplicationMutation } from "../../queries/funding-application/queries";

import { LandingScreen } from "./landing";

export const FundingLandingPage = () => {
  const {
    data: { customerUuid, customerDomain },
  } = useCustomerAppConfig();
  const { data: financingStage } = useSuspenseQuery(financingStageQueryOptions(customerUuid));

  const standardNavigate = useNavigate();
  const { navigate: companyCreationNavigate } = useSpaNavigate("CompanyCreation");

  const createFundingApplication = useCreateFundingApplicationMutation(customerDomain, customerUuid);

  const onSubmitAdvanceRequest = () => {
    return createFundingApplication
      .mutateAsync()
      .then(({ data }) => {
        if (data.subStage === "/revenue") {
          // Todo: navigate directly to /funding/application/revenue once OBN-202 is complete
          companyCreationNavigate("revenue");
        } else if (data.subStage) {
          standardNavigate(`/${customerDomain}/funding/application${data.subStage}`);
        }
      })
      .catch((error) => {
        // Error is already handled by the mutation's onError callback
        // This catch prevents unhandled promise rejection warnings
        addError("Failed to create funding application:", error);
      });
  };

  // Handle initial page load navigation only
  useEffect(() => {
    // Case 1: Wrong stage - redirect to appropriate page using standard navigate
    if (financingStage.stage !== "funding_application") {
      const path = [FinancingStagePath[financingStage.stage], financingStage.subStage].filter(Boolean).join("");
      const to = financingStage.stage === "company_creation" ? `/${path}` : `/${customerDomain}/${path}`;
      standardNavigate(to, { replace: true });
      return;
    }

    // Case 2: In-progress application - subStage is always a FundingApp path (e.g. /connections/banking)
    if (financingStage.subStage) {
      standardNavigate(`/${customerDomain}/funding/application${financingStage.subStage}`);
      return;
    }

    // Case 3: No subStage - stay on landing page (no action needed)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [financingStage?.stage, financingStage?.subStage]);

  // Render LandingScreen by default
  return financingStage?.stage === "funding_application" && !financingStage.subStage ? (
    <LandingScreen onSubmit={onSubmitAdvanceRequest} isPending={createFundingApplication.isPending} />
  ) : null;
};
